// src/components/consultorio/DocumentosTab.js

import React, { useState, useEffect, useCallback } from 'react';
import { ListGroup, Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileImage, faFileWord, faFileAlt, faTrash, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// Função auxiliar para escolher um ícone com base no tipo do arquivo
const getFileIcon = (mimeType) => {
  if (mimeType.includes('pdf')) return faFilePdf;
  if (mimeType.includes('image')) return faFileImage;
  if (mimeType.includes('word')) return faFileWord;
  return faFileAlt;
};

// --- Constantes para validação de upload ---
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
// Lista de tipos de arquivo permitidos para segurança
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
];
const MAX_DOCUMENTS_PER_CONSULTA = 10;

const DocumentosTab = ({ pacienteId, consultaId, tipoFiltro, readOnly = false, fetchOnMount = false }) => {
  const { currentEntidade } = useEntidadeContext();
  const [documentos, setDocumentos] = useState([]);
  const downloadDocument = async (doc) => {
    try {
      const response = await axios.get(`/api/documentos/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.nome_original || 'documento';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e.response?.status === 422 ? 'O arquivo não foi liberado pela verificação de segurança.' : e.response?.status === 503 ? 'A verificação de segurança está indisponível. Tente novamente mais tarde.' : 'Não foi possível baixar o documento nesta unidade.'); }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  // O limite de quantidade só se aplica se estivermos na aba "Documentos" de uma consulta específica.
  // Não se aplica a "Laudos" (que tem tipoFiltro) nem ao histórico geral (que é readOnly).
  const aplicaLimiteDeQuantidade = !!consultaId && !tipoFiltro && !readOnly;

  // Função para buscar os documentos do paciente no backend
  const fetchDocumentos = useCallback(async () => {
    if (!pacienteId) return;
    setIsLoading(true);
    try {
      // Constrói os parâmetros da query dinamicamente.
      const params = {};

      // Se um tipo de filtro for especificado (ex: "Laudo"), aplica-o.
      // Isso buscará todos os documentos daquele tipo para o paciente.
      if (tipoFiltro) {
        params.tipo = tipoFiltro;
      } 
      // Se NÃO for um filtro de tipo (ou seja, é a aba "Documentos"),
      // e uma consultaId for fornecida, filtramos por ela para mostrar apenas os da consulta atual.
      else if (consultaId) {
        params.consulta_id = consultaId;
      }

      // BACKEND: Endpoint para buscar documentos por paciente, agora com filtro opcional.
      const response = await axios.get(`/api/pacientes/${pacienteId}/documentos`, { params });
      setDocumentos(response.data.documentos || []);
      setError(null);
    } catch (err) {
      console.error("Erro ao buscar documentos:", err);
      setError("Não foi possível carregar os documentos.");
    } finally {
      setIsLoading(false);
    }
  }, [pacienteId, consultaId, tipoFiltro]);

  // Busca os documentos quando o componente é montado, mas apenas se fetchOnMount for true.
  useEffect(() => {
    if (fetchOnMount) {
      fetchDocumentos();
      const timer = setInterval(fetchDocumentos, 30000);
      window.addEventListener('patient-attachments-updated',fetchDocumentos);
      return () => { clearInterval(timer); window.removeEventListener('patient-attachments-updated',fetchDocumentos); };
    }
  }, [fetchOnMount, fetchDocumentos]);

  // Atualiza o estado quando um arquivo é selecionado no input
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // 1. Validação de tipo de arquivo
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setError(`Tipo de arquivo não permitido. Apenas: JPG, PNG, PDF, DOC, DOCX.`);
        setSelectedFile(null);
        event.target.value = ''; // Limpa o input
        return;
      }

      // 2. Validação de tamanho do arquivo
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`O arquivo é muito grande. O tamanho máximo é de ${MAX_FILE_SIZE_MB}MB.`);
        setSelectedFile(null);
        event.target.value = ''; // Limpa o input
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  // Função para fazer o upload do arquivo selecionado
  const handleUpload = async () => {
    // Validação do limite de arquivos por consulta, se aplicável
    if (aplicaLimiteDeQuantidade && documentos.length >= MAX_DOCUMENTS_PER_CONSULTA) {
        setError(`Limite de ${MAX_DOCUMENTS_PER_CONSULTA} documentos por consulta atingido.`);
        return;
    }

    if (!selectedFile) {
      setError("Por favor, selecione um arquivo para enviar.");
      return;
    }
    if (!pacienteId || !currentEntidade) {
        setError("Informações da consulta ou entidade não encontradas.");
        return;
    }

    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('documento', selectedFile);
    formData.append('entidade_id', currentEntidade.id);
    formData.append('paciente_id', pacienteId);
    // Associa o documento à consulta atual, se houver uma
    if (consultaId) formData.append('consulta_id', consultaId);
    // Envia o tipo do documento no upload, se especificado
    if (tipoFiltro) formData.append('tipo', tipoFiltro);

    try {
      // BACKEND: Endpoint para receber o upload do documento.
      const response = await axios.post(`/api/documentos/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 201) {
        // Adiciona o novo documento à lista e limpa o formulário
        setDocumentos(prev => [response.data.documento, ...prev]);
        setSelectedFile(null);
        document.getElementById(`documento-upload-input-${tipoFiltro || 'geral'}`).value = '';
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Ocorreu um erro ao enviar o documento.";
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  // Função para excluir um documento
  const handleDelete = async (documentoId) => {
    if (!window.confirm("Tem certeza que deseja excluir este documento?")) {
        return;
    }
    try {
        // BACKEND: Endpoint para excluir um documento.
        await axios.delete(`/api/documentos/${documentoId}`);
        setDocumentos(prev => prev.filter(doc => doc.id !== documentoId));
    } catch (err) {
        setError("Erro ao excluir o documento.");
    }
  };

  const isUploadDisabled = aplicaLimiteDeQuantidade && (documentos.length >= MAX_DOCUMENTS_PER_CONSULTA);

  return (
    <div>
      {/* Oculta o formulário de upload em modo de apenas leitura */}
      {!readOnly && (
        <Row className="mb-4 align-items-end">
          <Col md={8}>
            <Form.Group controlId={`documento-upload-input-${tipoFiltro || 'geral'}`}>
              <Form.Label>
                {tipoFiltro ? `Enviar novo ${tipoFiltro}` : 'Enviar novo documento'}
              </Form.Label>
              <Form.Control 
                  type="file" 
                  onChange={handleFileChange} 
                  disabled={isUploadDisabled}
              />
              <Form.Text className="text-muted">
                  Permitido: PDF, DOC, DOCX, JPG, PNG. Tamanho máx: {MAX_FILE_SIZE_MB}MB.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading || isUploadDisabled}
              className="w-100"
            >
              {isUploading ? <Spinner as="span" animation="border" size="sm" /> : 'Enviar'}
            </Button>
          </Col>
        </Row>
      )}

      {!readOnly && isUploadDisabled && (
        <Alert variant="info">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Você atingiu o limite de {MAX_DOCUMENTS_PER_CONSULTA} documentos para esta consulta.
        </Alert>
      )}

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <hr />

      <h5>
        {readOnly 
          ? 'Histórico de Documentos do Paciente' 
          : tipoFiltro 
            ? `${tipoFiltro.charAt(0).toUpperCase() + tipoFiltro.slice(1)}s Anexados` 
            : 'Documentos Anexados'
        }
        {aplicaLimiteDeQuantidade && (
          <span className="text-muted fs-6 ms-2">({documentos.length}/{MAX_DOCUMENTS_PER_CONSULTA})</span>
        )}
      </h5>
      <Button variant="outline-secondary" size="sm" className="mb-2" onClick={fetchDocumentos} disabled={isLoading}>Atualizar arquivos</Button>
      {isLoading ? (
        <div className="text-center"><Spinner animation="border" /></div>
      ) : (
        <ListGroup>
          {documentos.length > 0 ? documentos.map(doc => (
            <ListGroup.Item key={doc.id} className="d-flex justify-content-between align-items-center">
              <div>
                <FontAwesomeIcon icon={getFileIcon(doc.mime_type)} className="me-3" />
                <a href="#download" onClick={(event) => { event.preventDefault(); downloadDocument(doc); }}>
                  {doc.nome_original}
                </a>
                <small className="text-muted d-block">
                  Enviado em: {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                </small>
                {doc.patient_account_id && <small className="text-primary d-block">Enviado pelo paciente · Agendamento #{doc.agendamento_id}</small>}
                {doc.descricao && <small className="d-block">{doc.descricao}</small>}
                {doc.patient_reported_at && <Alert variant="warning" className="mt-2 mb-0">Paciente sinalizou envio incorreto: {doc.patient_report}</Alert>}
              </div>
              {!readOnly && !doc.patient_account_id && (
                <div>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(doc.id)} title="Excluir documento">
                      <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              )}
            </ListGroup.Item>
          )) : (
            <p className="text-muted">Nenhum {readOnly ? 'documento' : (tipoFiltro || 'documento')} encontrado para este paciente.</p>
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default DocumentosTab;
